/**
 * Entitlement store — the ONLY way features check access (brief §17).
 * Components never check platform or purchase state inline.
 */
import { isWpgFree, type WallpaperGroupId } from '@/patterns/wallpaper';
import { getPurchasesAdapter } from '@/services/purchases';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

export const FREE_PATTERN_IDS: readonly string[] = ['square', 'hex', 'triangle'];

/** Debug/tester builds unlock Pro permanently (set by the build, untangled-style). */
const FORCE_PRO = import.meta.env.VITE_FORCE_PRO === 'true';

export const useEntitlementStore = defineStore('entitlement', () => {
  const isPro = ref(FORCE_PRO);
  const purchasePending = ref(false);
  const initialised = ref(false);

  const adapter = getPurchasesAdapter();

  async function init(): Promise<void> {
    if (initialised.value) return;
    initialised.value = true;
    if (FORCE_PRO) return;
    adapter.onChange((pro) => {
      isPro.value = pro;
    });
    isPro.value = await adapter.isPro();
  }

  const canUseTrace = computed(() => isPro.value);
  const canUseFill = computed(() => isPro.value);
  /** Full export access — PSD, vector (SVG/DXF), and wallpaper sizes beyond
   *  the free tier (brief 12-vector_draw: export is no longer entirely
   *  paid-only, so this no longer gates the export sheet itself — only the
   *  still-locked modes/sizes inside it, and standalone PSD rendering). */
  const canExport = computed(() => isPro.value);

  /** Wallpaper (raster PNG) export is free at 2×2/3×3; every other size
   *  (4×4, custom) stays pro-only. */
  function canExportWallpaperSize(tilesAcross: number): boolean {
    return isPro.value || tilesAcross <= 3;
  }

  function isPatternUnlocked(patternId: string): boolean {
    return isPro.value || FREE_PATTERN_IDS.includes(patternId);
  }

  /** Wallpaper-group access — free tier gets the curated 7 (brief §2.1). */
  function isWpgUnlocked(wpgId: WallpaperGroupId): boolean {
    return isPro.value || isWpgFree(wpgId);
  }

  async function purchase(): Promise<void> {
    purchasePending.value = true;
    try {
      const result = await adapter.purchase();
      if (result.ok) isPro.value = true;
    } finally {
      purchasePending.value = false;
    }
  }

  async function restore(): Promise<void> {
    purchasePending.value = true;
    try {
      isPro.value = await adapter.restore();
    } finally {
      purchasePending.value = false;
    }
  }

  return {
    isPro,
    purchasePending,
    canUseTrace,
    canUseFill,
    canExport,
    canExportWallpaperSize,
    isPatternUnlocked,
    isWpgUnlocked,
    init,
    purchase,
    restore,
  };
});
